/**
 * The React chat composer, with the send features of the CRM chat window:
 * device photos/videos, vault, GIFs, recorded video, voice notes, PPV price
 * with a free preview, paid text, @-mentions, emoji (shortcut bar + full
 * picker), the script library, draft translation (Alt+R), replies and
 * "Copy to send" from the Transcript, and the prohibited-wording warning.
 * Create audio (ADR 0028) speaks typed text in the Account voice as a voice note.
 */
import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { FeatherIcon } from '../components/FeatherIcon';
import { PhoneSheet } from '../chats/phone/PhoneSheet';
import { Spinner, BusyLabel } from '../components/Spinner';
import { useIsNarrow } from '../hooks/useIsNarrow';
import { useVoicesLaunched } from '../voices/access';
import { CreateAudioPanel } from './CreateAudio';
import { composerTranslateTarget, emojiBarCollapsed, rememberEmoji, setEmojiBarCollapsed } from './composerPrefs';
import { EmojiBar, EmojiPicker } from './EmojiPicker';
import { TagCreatorPicker } from './TagCreatorPicker';
import { GifPicker, type Gif } from './GifPicker';
import type { ComposerFill } from './copyToSend';
import { useMediaUrl } from './media';
import { mentionAt, rankCreators, searchCreators, type Creator } from './mentions';
import { useProhibitedTerms } from './prohibited';
import { ScriptLibrary } from './ScriptLibrary';
import { PRICE_MAX, PRICE_MIN, type Draft } from './send';
import { detectLanguage, TranslateQuotaError, translateText } from './translate';
import { uploadToVault, uploadVoice, type MediaHandoff } from './upload';
import { formatSeconds, useRecorder } from './useRecorder';
import type { SendOptions, SendResult } from './useThread';
import type { VaultItem } from './vaultItem';
import { AccountContentPicker } from '../library/picker/AccountContentPicker';
import { prefetchAccountPicker } from '../library/picker/accountPrefetch';

interface Attachment extends VaultItem {
  key: string;
  uploading?: 'uploading' | 'processing';
  error?: string;
  /** Free preview inside a PPV. */
  preview?: boolean;
  /** A recorded voice note: local copy to listen back before sending. */
  voice?: { url: string; seconds: number };
  /** The voice note's transcoder handoff once uploaded (sent instead of a vault id). */
  handoff?: MediaHandoff;
  /** Made with Create audio: its log row, marked sent once the message goes out. */
  recordingId?: number | null;
}

export interface ComposerReply {
  id: string;
  text: string;
}

type Panel = 'emoji' | 'gif' | 'scripts' | 'tag' | 'audio' | null;

interface Props {
  accountId: string;
  fanId: string;
  modelName: string;
  onSend: (draft: Omit<Draft, 'fanId'>, opts?: SendOptions) => Promise<SendResult>;
  /** Focus the message field when the chat opens. */
  autoFocus?: boolean;
  /** Message being answered (Reply in the Transcript). */
  reply?: ComposerReply | null;
  onClearReply?: () => void;
  /** A message to load as the whole draft ("Copy to send" in the Transcript); `n` makes a repeat count. */
  fill?: { draft: ComposerFill; n: number } | null;
  /** The auto-translate toggle for fan messages (chat-composer-tools.js). */
  autoTranslate?: { on: boolean; toggle: () => void };
}

function AttachmentChip({ accountId, a, paid, onRemove, onTogglePreview }: { accountId: string; a: Attachment; paid: boolean; onRemove: () => void; onTogglePreview: () => void }) {
  const { t } = useTranslation('chat');
  const { url } = useMediaUrl(accountId, a.thumb);
  const [playing, setPlaying] = useState(false);
  if (a.voice) {
    return (
      <span className={`rc-attach rc-attach--voice ${a.error ? 'is-error' : ''}`} title={a.error || t('media.voice')}>
        <button type="button" className="rc-attach-play" onClick={() => setPlaying((v) => !v)} title={t('media.play')}>
          <FeatherIcon name={playing ? 'pause' : 'play'} size={13} />
        </button>
        {playing && <audio src={a.voice.url} autoPlay onEnded={() => setPlaying(false)} />}
        <span className="rc-attach-voice-len">{formatSeconds(a.voice.seconds)}</span>
        {a.uploading && <Spinner size={12} label={t(`composer.${a.uploading}`)} />}
        {a.error && <span className="rc-attach-voice-err">{t('composer.failed')}</span>}
        <button type="button" className="rc-attach-x" onClick={onRemove} title={t('composer.remove')}>
          <FeatherIcon name="x" size={12} />
        </button>
      </span>
    );
  }
  return (
    <span className={`rc-attach ${a.error ? 'is-error' : ''} ${paid ? (a.preview ? 'is-preview' : 'is-locked') : ''}`} title={a.error || a.label}>
      {url ? <img src={url} alt="" /> : <FeatherIcon name={a.type === 'video' ? 'video' : 'image'} size={16} />}
      {a.uploading && (
        <span className="rc-attach-state rc-attach-state--busy">
          <Spinner size={14} label={t(`composer.${a.uploading}`)} />
        </span>
      )}
      {a.error && <span className="rc-attach-state">{t('composer.failed')}</span>}
      {paid && !a.uploading && !a.error && (
        <button type="button" className="rc-attach-access" onClick={onTogglePreview} title={a.preview ? t('composer.makeLocked') : t('composer.makePreview')}>
          <FeatherIcon name={a.preview ? 'eye' : 'lock'} size={11} />
          {a.preview ? t('composer.previewShort') : t('composer.lockedShort')}
        </button>
      )}
      <button type="button" className="rc-attach-x" onClick={onRemove} title={t('composer.remove')}>
        <FeatherIcon name="x" size={12} />
      </button>
    </span>
  );
}

interface Tool {
  key: string;
  title: string;
  icon?: string;
  label?: string;
  on?: boolean;
  /** A switch: announced as pressed / not pressed. */
  toggle?: boolean;
  disabled?: boolean;
  onClick: () => void;
  /** The pointer rests on it / it gets focus: warm what it opens. */
  onHover?: () => void;
}

const TOOL_W = 35;

/**
 * The composer's tool buttons on one line: as many as fit, the rest in a
 * "more" menu, so Translate and Send always keep their place on the right.
 */
function ToolRow({ tools, narrow, onNarrow }: { tools: Tool[]; narrow: boolean; onNarrow: (narrow: boolean) => void }) {
  const { t } = useTranslation('chat');
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    // Measured now too: a hidden pane never fires ResizeObserver.
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);

  const fits = !width || tools.length * TOOL_W <= width ? tools.length : Math.max(0, Math.floor((width - TOOL_W) / TOOL_W));
  const shown = tools.slice(0, fits);
  const hidden = tools.slice(fits);
  // Fewer than half the tools fit: Translate drops its label to give them room.
  const isNarrow = width > 0 && fits < Math.ceil(tools.length / 2);
  useEffect(() => {
    if (isNarrow !== narrow) onNarrow(isNarrow);
  }, [isNarrow, narrow, onNarrow]);

  return (
    <div className="rc-tool-row" ref={box}>
      {shown.map((tool) => (
        <button key={tool.key} type="button" className={`rc-tool ${tool.label ? 'rc-tool--gif' : ''} ${tool.on ? 'is-on' : ''}`} data-tip={tool.title} aria-label={tool.title} aria-pressed={tool.toggle ? !!tool.on : undefined} disabled={tool.disabled} onClick={tool.onClick} onMouseEnter={tool.onHover} onFocus={tool.onHover}>
          {tool.icon ? <FeatherIcon name={tool.icon} size={18} /> : tool.label}
        </button>
      ))}
      {hidden.length > 0 && (
        <span className="rc-tool-more">
          <button type="button" className={`rc-tool ${open || hidden.some((x) => x.on) ? 'is-on' : ''}`} data-tip={open ? undefined : t('composer.moreTools')} aria-label={t('composer.moreTools')} onClick={() => setOpen((v) => !v)}>
            <FeatherIcon name="more-horizontal" size={18} />
          </button>
          {open && (
            <div className="rc-tool-menu" role="menu">
              {hidden.map((tool) => (
                <button
                  key={tool.key}
                  type="button"
                  role="menuitem"
                  className={tool.on ? 'is-on' : ''}
                  disabled={tool.disabled}
                  onClick={() => {
                    setOpen(false);
                    tool.onClick();
                  }}
                >
                  <span className="rc-tool-menu-ic">
                    <FeatherIcon name={tool.icon || 'film'} size={15} />
                  </span>
                  {tool.title}
                </button>
              ))}
            </div>
          )}
        </span>
      )}
    </div>
  );
}

function LivePreview({ stream }: { stream: MediaStream | null }) {
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (video.current) video.current.srcObject = stream;
  }, [stream]);
  return <video ref={video} className="rc-rec-video" autoPlay muted playsInline />;
}

export function Composer({ accountId, fanId, modelName, onSend, autoFocus, reply, onClearReply, fill, autoTranslate }: Props) {
  const { t } = useTranslation('chat');
  const [text, setText] = useState('');
  const [panel, setPanel] = useState<Panel>(null);
  const [emojiBar, setEmojiBar] = useState(() => !emojiBarCollapsed());
  const [emojiRev, setEmojiRev] = useState(0);
  const [vault, setVault] = useState(false);
  const [priceOpen, setPriceOpen] = useState(false);
  const [price, setPrice] = useState('');
  const [items, setItems] = useState<Attachment[]>([]);
  const [gif, setGif] = useState<Gif | null>(null);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<string[] | null>(null);
  const [undo, setUndo] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [mentionItems, setMentionItems] = useState<Creator[] | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [narrowTools, setNarrowTools] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);
  const phone = useIsNarrow();
  // Phones (ADR 0035): the tools live in the + sheet; the emoji bar shows only when picked there.
  const [toolsOpen, setToolsOpen] = useState(false);
  const [phoneEmoji, setPhoneEmoji] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const recorder = useRecorder();
  const createAudio = useVoicesLaunched();
  const flagged = useProhibitedTerms(accountId, text);

  // A draft belongs to one chat: switching chats starts clean.
  useEffect(() => {
    setText('');
    setItems((prev) => {
      prev.forEach((i) => i.voice && URL.revokeObjectURL(i.voice.url));
      return [];
    });
    setPrice('');
    setPriceOpen(false);
    setGif(null);
    setPanel(null);
    setNotice(null);
    setConfirm(null);
    setUndo(null);
    setMention(null);
    recorder.cancel();
    if (autoFocus) field.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, fanId, autoFocus]);

  useEffect(() => {
    if (reply) field.current?.focus();
  }, [reply]);

  // "Copy to send" replaces the draft: the copied message is what goes out, with only the price (or text) to change.
  useEffect(() => {
    if (!fill) return;
    const { draft } = fill;
    setItems((prev) => {
      prev.forEach((i) => i.voice && URL.revokeObjectURL(i.voice.url));
      return draft.media.map((x) => ({ ...x, key: `v-${x.id}` }));
    });
    setPrice(draft.price != null ? String(draft.price) : '');
    setPriceOpen(draft.price != null);
    setGif(draft.giphyId ? { id: draft.giphyId, url: `https://media.giphy.com/media/${encodeURIComponent(draft.giphyId)}/giphy.gif`, thumb: '', title: 'GIF' } : null);
    setConfirm(null);
    setPanel(null);
    setMention(null);
    setNotice(draft.skippedMedia ? t('composer.fanMediaSkipped') : null);
    onClearReply?.();
    setField(draft.text, draft.text.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fill]);

  // @-mention list: the first page right away, OnlyFans' name search from two characters.
  useEffect(() => {
    if (!mention) {
      setMentionItems(null);
      return;
    }
    let live = true;
    const q = mention.query;
    const base = searchCreators(accountId, '').catch(() => [] as Creator[]);
    void base.then((first) => live && setMentionItems((prev) => (prev && prev.length ? prev : rankCreators(q, [first]))));
    const timer = window.setTimeout(
      () => {
        void Promise.all([q.length >= 2 ? searchCreators(accountId, q).catch(() => [] as Creator[]) : Promise.resolve([] as Creator[]), base]).then(
          ([searched, first]) => live && setMentionItems(rankCreators(q, [searched, first])),
        );
      },
      q.length >= 2 ? 350 : 0,
    );
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [mention, accountId]);

  const priceValue = price.trim() ? Number(price.replace(',', '.')) : null;
  const paid = priceValue != null && priceValue > 0;
  const content = items.filter((i) => !i.voice);
  const ready = items.every((i) => !i.uploading && !i.error);
  // Sends are queued into the Transcript at once, so the next one can be written straight away.
  const canSend = !recorder.kind && ready && (text.trim().length > 0 || items.length > 0 || !!gif);

  function setField(next: string, caret?: number) {
    setText(next);
    if (undo != null) setUndo(null);
    if (caret != null) {
      requestAnimationFrame(() => {
        field.current?.focus();
        field.current?.setSelectionRange(caret, caret);
      });
    }
  }

  function insertText(value: string) {
    const el = field.current;
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    setField(text.slice(0, start) + value + text.slice(end), start + value.length);
  }

  function readMention(value: string, caret: number) {
    const hit = mentionAt(value, caret);
    setMention((prev) => (hit ? (prev && prev.start === hit.start && prev.query === hit.query ? prev : hit) : null));
    if (!hit) setMentionIndex(0);
  }

  function pickMention(c: Creator) {
    if (!mention) return;
    const caret = field.current?.selectionStart ?? text.length;
    const insert = `@${c.username} `;
    setField(text.slice(0, mention.start) + insert + text.slice(caret), mention.start + insert.length);
    setMention(null);
  }

  function startMention() {
    const caret = field.current?.selectionStart ?? text.length;
    const lead = caret > 0 && !/\s/.test(text[caret - 1] ?? '') ? ' @' : '@';
    const next = text.slice(0, caret) + lead + text.slice(caret);
    setField(next, caret + lead.length);
    setMention({ start: caret + lead.length - 1, query: '' });
  }

  function clearDraft(revoke = true) {
    setText('');
    setItems((prev) => {
      // A queued draft may still be restored (flagged wording), so its voice copy stays playable.
      if (revoke) prev.forEach((i) => i.voice && URL.revokeObjectURL(i.voice.url));
      return [];
    });
    setPrice('');
    setPriceOpen(false);
    setGif(null);
    setConfirm(null);
    setUndo(null);
    onClearReply?.();
  }

  async function submit(confirmed = false) {
    if (!canSend && !confirmed) return;
    // Flagged wording is confirmed before anything leaves (CRM confirmChatProhibitedTerms).
    if (!confirmed && flagged.length) {
      setConfirm(flagged);
      return;
    }
    setNotice(null);
    const snapshot = { text, items, price, priceOpen, gif };
    setSending(true);
    const result = await onSend(
      {
        text,
        media: items.map((i) => ({ id: i.id, preview: !!i.preview, voice: !!i.voice, handoff: i.handoff, type: i.type, thumb: i.thumb })),
        price: priceValue,
        replyTo: reply ? { text: reply.text } : null,
        giphyId: gif?.id ?? null,
      },
      {
        confirmed,
        onQueued: () => {
          setSending(false);
          clearDraft(false);
          field.current?.focus();
        },
      },
    );
    setSending(false);
    if (result.ok) {
      // Voice notes made with Create audio: their log rows learn the message that carried them.
      for (const i of snapshot.items) if (i.recordingId) void api.markAudioSent(accountId, i.recordingId, result.messageId ?? null).catch(() => undefined);
      return;
    }
    if ('confirmTerms' in result) {
      // Nothing was sent: put the draft back for the decision.
      setText(snapshot.text);
      setItems(snapshot.items);
      setPrice(snapshot.price);
      setPriceOpen(snapshot.priceOpen);
      setGif(snapshot.gif);
      setConfirm(result.confirmTerms);
    } else if ('refused' in result) setNotice(t(`composer.refused.${result.refused}`, { min: PRICE_MIN, max: PRICE_MAX }));
    else if (result.partial) setNotice(t('composer.gifFailed', { error: result.error }));
    else setNotice(t('composer.sendFailed', { error: result.error }));
  }

  /** Script library "Send now": a text of its own, only with nothing else in the draft. */
  async function sendScript(value: string): Promise<boolean> {
    if (items.length || gif || paid || reply) {
      setNotice(t('composer.scriptDraftBusy'));
      return false;
    }
    setNotice(null);
    const result = await onSend({ text: value, media: [], price: null });
    if (result.ok) return true;
    if ('confirmTerms' in result) {
      setText(value);
      setConfirm(result.confirmTerms);
    } else setNotice(t('composer.sendFailed', { error: 'error' in result ? result.error : result.refused }));
    return false;
  }

  async function translateDraft() {
    if (undo != null) {
      setText(undo);
      setUndo(null);
      field.current?.focus();
      return;
    }
    if (!text.trim() || translating) return;
    setTranslating(true);
    setNotice(null);
    try {
      const out = await translateText(text, composerTranslateTarget());
      const before = text;
      setText(out);
      setUndo(before);
      field.current?.focus();
    } catch (err) {
      setNotice(err instanceof TranslateQuotaError ? t('composer.translateQuota') : t('composer.translateFailed', { error: (err as Error).message }));
    } finally {
      setTranslating(false);
    }
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (mention && mentionItems?.length) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const n = mentionItems.length;
        setMentionIndex((i) => (i + (e.key === 'ArrowDown' ? 1 : -1) + n) % n);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickMention(mentionItems[Math.min(mentionIndex, mentionItems.length - 1)]!);
        return;
      }
    }
    if (e.key === 'Escape' && mention) {
      e.preventDefault();
      setMention(null);
      return;
    }
    if (e.altKey && !e.ctrlKey && !e.metaKey && (e.key.toLowerCase() === 'r' || e.code === 'KeyR')) {
      e.preventDefault();
      void translateDraft();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  }

  function addUploads(files: File[]) {
    for (const file of files) {
      if (!/^(image|video)\//.test(file.type)) {
        setNotice(t('composer.onlyPhotoVideo'));
        continue;
      }
      const key = `up-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setItems((prev) => [...prev, { key, id: '', type: file.type.startsWith('video/') ? 'video' : 'photo', thumb: null, label: file.name, uploading: 'uploading' }]);
      uploadToVault(accountId, fanId, file, (stage) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, uploading: stage } : i))))
        .then((done) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, id: done.id, uploading: undefined } : i))))
        .catch((err) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, uploading: undefined, error: (err as Error).message } : i))));
    }
  }

  async function startRecording(kind: 'audio' | 'video') {
    if (recorder.kind) return;
    if (kind === 'audio' && items.some((i) => i.voice)) {
      setNotice(t('composer.oneVoice'));
      return;
    }
    setPanel(null);
    setNotice(null);
    try {
      await recorder.start(kind);
    } catch (err) {
      setNotice((err as Error).message === 'unsupported' ? t('composer.recordUnsupported') : t('composer.recordFailed', { error: (err as Error).message }));
    }
  }

  async function stopRecording() {
    const kind = recorder.kind;
    const done = await recorder.stop();
    if (!done) {
      setNotice(t('composer.recordEmpty'));
      return;
    }
    if (kind === 'video') {
      URL.revokeObjectURL(done.url);
      addUploads([done.file]);
      return;
    }
    attachVoice(done);
  }

  /** A voice note (recorded or created) into the draft, uploaded to OnlyFans' transcoder. */
  function attachVoice(done: { file: File; url: string; seconds: number; recordingId?: number | null }) {
    const key = `voice-${Date.now()}`;
    setItems((prev) => [...prev, { key, id: '', type: 'audio', thumb: null, label: t('media.voice'), uploading: 'uploading', voice: { url: done.url, seconds: done.seconds }, recordingId: done.recordingId }]);
    uploadVoice(accountId, fanId, done.file)
      .then((up) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, handoff: up.handoff, uploading: undefined } : i))))
      .catch((err) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, uploading: undefined, error: (err as Error).message } : i))));
  }

  function togglePreview(key: string) {
    setItems((prev) => {
      const target = prev.find((i) => i.key === key);
      if (!target) return prev;
      const on = !target.preview;
      // With several items only one can be the free preview.
      return prev.map((i) => (i.voice ? i : i.key === key ? { ...i, preview: on } : content.length > 1 && on ? { ...i, preview: false } : i));
    });
  }

  const togglePanel = (p: Exclude<Panel, null>) => setPanel((cur) => (cur === p ? null : p));
  function openCreateAudio() {
    if (panel !== 'audio' && items.some((i) => i.voice)) {
      setNotice(t('composer.oneVoice'));
      return;
    }
    setNotice(null);
    togglePanel('audio');
  }
  const translateLabel = (() => {
    const target = composerTranslateTarget().toUpperCase();
    const source = text.trim() ? detectLanguage(text).toUpperCase() : '';
    return source && source !== target ? `${source} → ${target}` : `→ ${target}`;
  })();
  const previewCount = content.filter((i) => i.preview).length;

  // In order of importance: what does not fit goes into the "more" menu from the end.
  const tools: Tool[] = [
    { key: 'upload', title: t('composer.upload'), icon: 'image', onClick: () => fileInput.current?.click() },
    { key: 'vault', title: t('composer.vault'), icon: 'archive', onClick: () => setVault(true), onHover: () => prefetchAccountPicker(accountId) },
    { key: 'price', title: t('composer.price'), icon: 'tag', on: priceOpen || paid, onClick: () => setPriceOpen((v) => !v) },
    { key: 'gif', title: t('composer.gif'), label: 'GIF', on: panel === 'gif' || !!gif, onClick: () => togglePanel('gif') },
    { key: 'voice', title: t('composer.voice'), icon: 'mic', on: recorder.kind === 'audio', disabled: !!recorder.kind, onClick: () => void startRecording('audio') },
    { key: 'scripts', title: t('composer.scripts'), icon: 'book-open', on: panel === 'scripts', onClick: () => togglePanel('scripts') },
    {
      key: 'emoji',
      title: t('composer.emoji'),
      icon: 'smile',
      on: emojiBar,
      onClick: () => {
        setEmojiBarCollapsed(emojiBar);
        setEmojiBar(!emojiBar);
      },
    },
    { key: 'video', title: t('composer.recordVideo'), icon: 'video', on: recorder.kind === 'video', disabled: !!recorder.kind, onClick: () => void startRecording('video') },
    { key: 'mention', title: t('composer.mention'), icon: 'at-sign', onClick: startMention },
    { key: 'tag', title: t('composer.tagCreator'), icon: 'user-plus', on: panel === 'tag', onClick: () => togglePanel('tag') },
    ...(autoTranslate
      ? [{ key: 'auto-translate', title: t('composer.autoTranslate'), icon: 'globe', on: autoTranslate.on, toggle: true, onClick: autoTranslate.toggle }]
      : []),
  ];

  /** A tool from the phone's + sheet: the sheet closes, the tool runs (in the same tap, so a file picker still opens). */
  const fromSheet = (run: () => void) => () => {
    setToolsOpen(false);
    run();
  };
  const sheetTools: Tool[] = phone
    ? [
        { key: 'upload', title: t('composer.gallery'), icon: 'image', onClick: () => fileInput.current?.click() },
        { key: 'vault', title: t('composer.vault'), icon: 'archive', onClick: () => setVault(true) },
        {
          key: 'paid',
          title: t('composer.paidMessage'),
          icon: 'lock',
          on: paid,
          onClick: () => {
            setPriceOpen(true);
            setVault(true);
          },
        },
        { key: 'voice', title: t('composer.voiceNote'), icon: 'mic', disabled: !!recorder.kind, onClick: () => void startRecording('audio') },
        ...(createAudio ? [{ key: 'ai-audio', title: t('createAudio.button'), icon: 'volume-2', on: panel === 'audio', disabled: !!recorder.kind, onClick: openCreateAudio }] : []),
        { key: 'price', title: t('composer.price'), icon: 'tag', on: priceOpen || paid, onClick: () => setPriceOpen((v) => !v) },
        { key: 'gif', title: t('composer.gif'), label: 'GIF', on: panel === 'gif' || !!gif, onClick: () => togglePanel('gif') },
        { key: 'scripts', title: t('composer.scripts'), icon: 'book-open', on: panel === 'scripts', onClick: () => togglePanel('scripts') },
        { key: 'emoji', title: t('composer.emoji'), icon: 'smile', on: phoneEmoji, onClick: () => setPhoneEmoji((v) => !v) },
        { key: 'video', title: t('composer.recordVideo'), icon: 'video', disabled: !!recorder.kind, onClick: () => void startRecording('video') },
        { key: 'tag', title: t('composer.tagCreator'), icon: 'user-plus', on: panel === 'tag', onClick: () => togglePanel('tag') },
        { key: 'mention', title: t('composer.mention'), icon: 'at-sign', onClick: startMention },
        { key: 'translate', title: undo != null ? t('composer.undoTranslate') : t('composer.translateDraft'), icon: undo != null ? 'rotate-ccw' : 'globe', disabled: translating || (!text.trim() && undo == null), onClick: () => void translateDraft() },
        ...(autoTranslate ? [{ key: 'auto-translate', title: t('composer.autoTranslate'), icon: 'globe', on: autoTranslate.on, toggle: true, onClick: autoTranslate.toggle }] : []),
      ]
    : [];
  const fieldBlock = (
    <div className="rc-field-wrap">
      {mention && (
        <div className="rc-mention" role="listbox">
          {mentionItems == null && <span className="rc-mention-empty"><Spinner size={14} label={t('mention.loading')} /></span>}
          {mentionItems && !mentionItems.length && <span className="rc-mention-empty">{t('mention.none')}</span>}
          {mentionItems?.map((c, i) => (
            <button
              key={c.username}
              type="button"
              role="option"
              aria-selected={i === mentionIndex}
              className={i === mentionIndex ? 'is-on' : ''}
              onMouseDown={(e) => (e.preventDefault(), pickMention(c))}
            >
              <span className="rc-mention-ini">{(c.name || c.username).slice(0, 1).toUpperCase()}</span>
              <b>{c.name}</b>
              <span className="rc-muted">@{c.username}</span>
            </button>
          ))}
        </div>
      )}
      <textarea
        ref={field}
        value={text}
        rows={phone ? 1 : 2}
        placeholder={t('composer.placeholder', { model: modelName })}
        onChange={(e) => {
          setField(e.target.value);
          readMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
        }}
        onClick={(e) => readMention(e.currentTarget.value, e.currentTarget.selectionStart ?? 0)}
        onBlur={() => window.setTimeout(() => setMention(null), 150)}
        onKeyDown={onKey}
        onPaste={(e) => {
          const files = Array.from(e.clipboardData?.files || []).filter((f) => /^(image|video)\//.test(f.type));
          if (files.length) {
            e.preventDefault();
            addUploads(files);
          }
        }}
      />
    </div>
  );
  const idleSendLabel = paid ? t('composer.sendPaid', { price: `$${priceValue!.toFixed(2)}` }) : t('composer.send');
  const sendLabel = sending ? t('composer.sending') : idleSendLabel;

  return (
    <div className={`rc-composer-wrap ${phone ? 'is-phone' : ''}`}>
      {panel === 'emoji' && (
        <EmojiPicker
          onPick={(e) => {
            insertText(e);
            rememberEmoji(e);
          }}
          onClose={() => setPanel(null)}
          onShortcutsChange={() => setEmojiRev((n) => n + 1)}
        />
      )}
      {panel === 'gif' && (
        <GifPicker
          accountId={accountId}
          fanId={fanId}
          onClose={() => setPanel(null)}
          onPick={(g) => {
            setGif(g);
            setPanel(null);
            field.current?.focus();
          }}
        />
      )}
      {panel === 'tag' && (
        <TagCreatorPicker
          accountId={accountId}
          onClose={() => setPanel(null)}
          onPick={(c) => {
            const caret = field.current?.selectionStart ?? text.length;
            const lead = caret > 0 && !/\s/.test(text[caret - 1] ?? '') ? ' ' : '';
            insertText(`${lead}@${c.username} `);
            setPanel(null);
            field.current?.focus();
          }}
        />
      )}
      {panel === 'audio' && (
        <CreateAudioPanel
          accountId={accountId}
          fanId={fanId}
          initialText={text}
          onClose={() => setPanel(null)}
          onCreated={(note, spoken) => {
            attachVoice(note);
            setPanel(null);
            // The line came from the message field: it is now said out loud, not written too.
            if (spoken.trim() === text.trim()) setField('');
            field.current?.focus();
          }}
        />
      )}
      {panel === 'scripts' && <ScriptLibrary accountId={accountId} onClose={() => setPanel(null)} onInsert={(v) => (insertText(v), setPanel(null))} onSend={sendScript} />}
      {(phone ? phoneEmoji : emojiBar) && <EmojiBar key={emojiRev} onPick={insertText} onOpenAll={() => togglePanel('emoji')} />}
      {confirm && (
        <div className="rc-confirm" role="alertdialog">
          <FeatherIcon name="alert-triangle" size={16} />
          <div className="rc-confirm-text">
            <b>{t('composer.prohibitedTitle')}</b>
            <span>{confirm.length ? t('composer.prohibitedTerms', { terms: confirm.join(', ') }) : t('composer.prohibitedGeneric')}</span>
          </div>
          <button type="button" className="rc-btn" onClick={() => setConfirm(null)}>
            {t('composer.edit')}
          </button>
          <button type="button" className="rc-btn rc-btn--primary" onClick={() => void submit(true)} disabled={sending}>
            {t('composer.sendAnyway')}
          </button>
        </div>
      )}
      {notice && (
        <div className="rc-notice">
          {notice}
          <button type="button" className="rc-notice-x" onClick={() => setNotice(null)} title={t('common.close')}>
            <FeatherIcon name="x" size={12} />
          </button>
        </div>
      )}
      {recorder.kind && (
        <div className="rc-rec">
          {recorder.kind === 'video' && <LivePreview stream={recorder.stream} />}
          <span className="rc-rec-dot" />
          <span className="rc-rec-label">{recorder.kind === 'video' ? t('composer.recordingVideo') : t('composer.recordingVoice')}</span>
          <span className="rc-rec-time">{formatSeconds(recorder.seconds)}</span>
          <span style={{ flex: 1 }} />
          <button type="button" className="rc-btn" onClick={recorder.cancel}>
            {t('common.cancel')}
          </button>
          <button type="button" className="rc-btn rc-btn--primary" onClick={() => void stopRecording()}>
            <FeatherIcon name="square" size={12} />
            {t('composer.stopRecording')}
          </button>
        </div>
      )}
      <div className={`rc-composer ${paid ? 'is-paid' : ''}`}>
        {reply && (
          <div className="rc-reply-chip">
            <FeatherIcon name="corner-up-left" size={13} />
            <span>{reply.text.slice(0, 120) || `#${reply.id}`}</span>
            <button type="button" onClick={onClearReply} title={t('composer.cancelReply')}>
              <FeatherIcon name="x" size={12} />
            </button>
          </div>
        )}
        {paid && (
          <div className="rc-paid-head">
            <FeatherIcon name="lock" size={13} />
            <span>
              {t('composer.ppvSummary', { price: `$${priceValue!.toFixed(2)}` })}
              {content.length > 0 && <> · {t('composer.ppvItems', { locked: content.length - previewCount, previews: previewCount })}</>}
              {!content.length && <> · {t('composer.paidText')}</>}
            </span>
            {content.length > 0 && <span className="rc-muted">{t('composer.previewHint')}</span>}
          </div>
        )}
        {(items.length > 0 || gif) && (
          <div className="rc-attachments">
            {items.map((a) => (
              <AttachmentChip
                key={a.key}
                accountId={accountId}
                a={a}
                paid={paid}
                onTogglePreview={() => togglePreview(a.key)}
                onRemove={() =>
                  setItems((prev) => {
                    if (a.voice) URL.revokeObjectURL(a.voice.url);
                    return prev.filter((i) => i.key !== a.key);
                  })
                }
              />
            ))}
            {gif && (
              <span className="rc-attach rc-attach--gif" title={gif.title || 'GIF'}>
                <img src={gif.thumb || gif.url} alt="GIF" />
                <span className="rc-attach-state">GIF</span>
                <button type="button" className="rc-attach-x" onClick={() => setGif(null)} title={t('composer.remove')}>
                  <FeatherIcon name="x" size={12} />
                </button>
              </span>
            )}
          </div>
        )}
        {!phone && fieldBlock}
        {flagged.length > 0 && (
          <div className="rc-flagged" role="status">
            <FeatherIcon name="alert-triangle" size={12} />
            {t('composer.flagged', { terms: flagged.join(', ') })}
          </div>
        )}
        {(priceOpen || paid) && (
          <div className="rc-price-row">
            <label className="rc-price">
              <FeatherIcon name="lock" size={13} />
              <span>{t('composer.ppvPrice')}</span>
              <span className="rc-price-input">
                $
                <input inputMode="decimal" value={price} placeholder={t('composer.free')} autoFocus={priceOpen && !paid} onChange={(e) => setPrice(e.target.value.replace(/[^\d.,]/g, ''))} />
              </span>
              <button type="button" className="rc-price-x" title={t('composer.clearPrice')} onClick={() => (setPrice(''), setPriceOpen(false))}>
                <FeatherIcon name="x" size={12} />
              </button>
            </label>
          </div>
        )}
        <input ref={fileInput} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => (addUploads(Array.from(e.target.files || [])), (e.target.value = ''))} />
        {phone && (
          <div className="rc-prow">
            <button type="button" className={`rc-prow-plus ${toolsOpen ? 'is-on' : ''}`} aria-label={t('composer.tools')} aria-haspopup="dialog" onClick={() => setToolsOpen(true)}>
              <FeatherIcon name="plus" size={22} />
            </button>
            {fieldBlock}
            {canSend || sending ? (
              <button type="button" className={`rc-prow-send ${paid ? 'is-paid' : ''}`} disabled={!canSend} aria-label={sendLabel} onClick={() => void submit()}>
                {paid && <span>${priceValue!.toFixed(2)}</span>}
                <FeatherIcon name="arrow-up" size={20} />
              </button>
            ) : (
              <button type="button" className="rc-prow-mic" aria-label={t('composer.voiceNote')} disabled={!!recorder.kind} onClick={() => void startRecording('audio')}>
                <FeatherIcon name="mic" size={20} />
              </button>
            )}
          </div>
        )}
        {!phone && (
        <div className="rc-tools">
          <ToolRow tools={tools} narrow={narrowTools} onNarrow={setNarrowTools} />
          <button
            type="button"
            className={`rc-translate ${undo != null ? 'is-undo' : ''} ${narrowTools ? 'is-compact' : ''}`}
            data-tip={t('composer.translateHint')}
            aria-label={t('composer.translateHint')}
            disabled={translating || (!text.trim() && undo == null)}
            onClick={() => void translateDraft()}
          >
            {translating ? <Spinner size={12} label={t('composer.translating')} /> : <FeatherIcon name={undo != null ? 'rotate-ccw' : 'globe'} size={13} />}
            <span className="rc-translate-label">{undo != null ? t('composer.undoTranslate') : translateLabel}</span>
          </button>
          {createAudio && (
            <button
              type="button"
              className={`rc-ai-audio ${panel === 'audio' ? 'is-on' : ''}`}
              data-tip={t('createAudio.tool')}
              aria-label={t('createAudio.tool')}
              aria-pressed={panel === 'audio'}
              disabled={!!recorder.kind}
              onClick={openCreateAudio}
            >
              <FeatherIcon name="volume-2" size={16} />
              <span className="rc-ai-audio-label">{t('createAudio.button')}</span>
            </button>
          )}
          <button type="button" className="rc-send" disabled={!canSend} onClick={() => void submit()} data-tip={t('composer.enterToSend')}>
            <span className="rc-send-label"><BusyLabel busy={sending} label={sendLabel}>{idleSendLabel}</BusyLabel></span>
            <FeatherIcon name="arrow-up" size={14} />
          </button>
        </div>
        )}
      </div>
      {!phone && <div className="rc-hint">{t('composer.hint')}</div>}
      {phone && (
        <PhoneSheet open={toolsOpen} onClose={() => setToolsOpen(false)} label={t('composer.tools')}>
          <div className="rc-tool-grid">
            {sheetTools.map((tool) => (
              <button
                key={tool.key}
                type="button"
                className={`rc-tool-tile ${tool.on ? 'is-on' : ''}`}
                disabled={tool.disabled}
                aria-pressed={tool.toggle ? !!tool.on : undefined}
                onClick={fromSheet(tool.onClick)}
              >
                <span className="rc-tool-tile-ic">{tool.icon ? <FeatherIcon name={tool.icon} size={25} /> : <b>{tool.label}</b>}</span>
                <span className="rc-tool-tile-label">{tool.title}</span>
              </button>
            ))}
          </div>
        </PhoneSheet>
      )}
      {vault && (
        <AccountContentPicker
          accountId={accountId}
          initial={items.filter((i) => i.key.startsWith('v-')).map((i) => ({ id: Number(i.id), type: i.type, thumb: i.thumb ?? '' }))}
          onClose={() => setVault(false)}
          onDone={(picked) => {
            setItems((prev) => {
              const kept = new Map(prev.filter((i) => i.key.startsWith('v-')).map((i) => [i.id, i]));
              const attached = picked.map((m) => {
                const id = String(m.id);
                const before = kept.get(id);
                return { id, type: m.type, thumb: before?.thumb ?? (m.small || m.thumb || null), label: before?.label ?? m.type, key: `v-${id}`, preview: before?.preview };
              });
              return [...prev.filter((i) => !i.key.startsWith('v-')), ...attached];
            });
            setVault(false);
            field.current?.focus();
          }}
        />
      )}
    </div>
  );
}
